import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Tooltip } from '@mui/material';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import {
  BookAbsentMark,
  BookCellCount,
  BookCell,
  BookDetailLine,
  BookDetail,
  BookFootCell,
  BookFootName,
  BookFootSummary,
  BookGrid,
  BookHeadCell,
  BookHeadName,
  BookHeadSummary,
  BookLegendItem,
  BookLegendUnit,
  BookLegend,
  BookMark,
  BookMonthCount,
  BookName,
  BookScroll,
  BookSummaryCell,
  BookSummaryValue,
  BookTip,
  EmptyState,
  EmptyTitle,
  EmptyDescription,
  BOOK_SUMMARY_WIDTH,
} from './MemberGroupModalStyled';
import { bookTipOf, bookWeekLabel } from './attendanceShared';

/**
 * 출석부 뷰. 기간을 격자로 펴서 회원별로 그날 왔는지를 보여준다.
 *
 * <p>격자 계산은 attendanceBook.js 의 buildAttendanceBook 이 끝내 두었고 여기서는
 * 그리기만 한다. 그래서 받는 것이 book 하나뿐이다.
 */
const AttendanceBookView = ({ book }) => {
  const todayKey = moment().format('YYYY-MM-DD');
  const { t } = useTranslation();
  return (
    !book || book.members.length === 0 ? (
      <EmptyState>
        <EventBusyOutlinedIcon sx={{ fontSize: 34, color: '#ddd' }} />
        <EmptyTitle>{t('이 그룹에 회원이 없습니다')}</EmptyTitle>
        <EmptyDescription>{t('회원을 추가하면 출석부에 줄이 생깁니다')}</EmptyDescription>
      </EmptyState>
    ) : (
      <>
        <BookLegend>
          <BookLegendItem><BookMark tone="visited" />{t('수업 방문')}</BookLegendItem>
          <BookLegendItem><BookMark tone="free" />{t('자유 방문')}</BookLegendItem>
          <BookLegendItem><BookAbsentMark>✕</BookAbsentMark>{t('예약했지만 안 옴')}</BookLegendItem>
          <BookLegendItem><BookMark tone="reserved" />{t('예정')}</BookLegendItem>
          <BookLegendUnit>
            {book.unit === 'month'
              ? t('달마다 한 칸 · 숫자는 방문 일수입니다')
              : book.unit === 'weekly'
                ? t('주마다 한 칸 · 숫자는 방문 일수입니다')
                : t('하루마다 한 칸 · 방문은 하루 한 번으로 셉니다')}
          </BookLegendUnit>
        </BookLegend>

        <BookScroll>
          <BookGrid columns={book.columns.length} unit={book.unit}>
            <BookHeadName>{t('회원')}</BookHeadName>
            {book.columns.map(column => (
              <BookHeadCell key={column.key} isWeekend={column.isWeekend} isToday={column.key === todayKey}>
                <span className="label">{column.label}</span>
                {column.sub && <span className="sub">{column.sub}</span>}
              </BookHeadCell>
            ))}
            <BookHeadSummary offset={BOOK_SUMMARY_WIDTH}>{t('방문')}</BookHeadSummary>
            <BookHeadSummary offset={0}>{t('결석')}</BookHeadSummary>

            {book.members.map(member => (
              <React.Fragment key={member.memberId}>
                <BookName title={member.name}>{member.name}</BookName>
                {book.columns.map(column => {
                  const cell = member.cells.get(column.key);
                  if (!cell) {
                    return <BookCell key={column.key} isWeekend={column.isWeekend} unit={book.unit} isToday={column.key === todayKey} />;
                  }
                  // 하루는 한 상태만 갖지만, 주·달로 접으면 한 칸에 여러 날이 겹친다.
                  const tone = cell.visited > 0 ? 'visited' : cell.absent > 0 ? 'absent' : 'reserved';
                  const tip = bookTipOf(cell);
                  return (
                    <Tooltip key={column.key} arrow placement="top" title={<BookTip>{tip}</BookTip>}>
                      <BookCell isWeekend={column.isWeekend} unit={book.unit} isToday={column.key === todayKey}>
                        {book.unit === 'month' || book.unit === 'weekly' ? (
                          <BookMonthCount tone={tone}>{cell.visited}</BookMonthCount>
                        ) : book.unit === 'week' ? (
                          <BookDetail>
                            <BookDetailLine tone={tone}>
                              {cell.visited > 0 ? <BookMark tone={cell.freeEntry ? 'free' : 'visited'} />
                                : cell.absent > 0 ? <BookAbsentMark>✕</BookAbsentMark>
                                  : <BookMark tone="reserved" />}
                              {bookWeekLabel(cell)}
                            </BookDetailLine>
                            {cell.items.length > 1 && (
                              <BookCellCount>{`외 ${cell.items.length - 1}건`}</BookCellCount>
                            )}
                          </BookDetail>
                        ) : cell.visited > 0 ? (
                          <BookMark tone={cell.freeEntry ? 'free' : 'visited'} />
                        ) : cell.absent > 0 ? (
                          <BookAbsentMark>✕</BookAbsentMark>
                        ) : (
                          <BookMark tone="reserved" />
                        )}
                      </BookCell>
                    </Tooltip>
                  );
                })}
                <BookSummaryCell offset={BOOK_SUMMARY_WIDTH}>
                  <BookSummaryValue tone="visited">{member.visited}</BookSummaryValue>
                </BookSummaryCell>
                <BookSummaryCell offset={0}>
                  <BookSummaryValue tone={member.absent > 0 ? 'absent' : 'muted'}>
                    {member.absent}
                  </BookSummaryValue>
                </BookSummaryCell>
              </React.Fragment>
            ))}

            {/* 맨 아래 합계 — 그날 다녀간 인원 수 */}
            <BookFootName>{t('방문 인원')}</BookFootName>
            {book.columns.map(column => (
              <BookFootCell key={column.key} isWeekend={column.isWeekend} unit={book.unit}>
                {book.totals.get(column.key) || ''}
              </BookFootCell>
            ))}
            <BookFootSummary offset={BOOK_SUMMARY_WIDTH} />
            <BookFootSummary offset={0} />
          </BookGrid>
        </BookScroll>
      </>
    )
  );
};

export default AttendanceBookView;
